import { useEffect, useState } from 'react';
import {
  collection, onSnapshot, orderBy, query, where, limit,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, db } from './config';
import { FUNCTIONS_REGION } from './region';

/**
 * The الكبسولة الصوتية, from the client's side — م-3-04 to م-3-08.
 *
 * A note's DOCUMENT is read straight from Firestore, because a guardian
 * needs to see that one arrived and whether it has been heard. The
 * RECORDING is not in Firestore and not behind a URL: it comes back from a
 * callable that checks who is asking. That is the whole of م-3-07 — there
 * is nothing to download because nothing is ever addressable.
 */

const fns = () => getFunctions(app, FUNCTIONS_REGION);

/** م-3-05, the device half. The server enforces it again, and in bytes. */
export const MAX_DURATION_MS = 30 * 1000;

export type VoiceNote = {
  id: string;
  studentId: string;
  muqriId: string;
  durationMs: number;
  createdAt: number;
  expiresAt: number;
  heardAt: number | null;
};

/** The audio itself, held only for as long as it is being played. */
export type PlayableNote = {
  audio: string;
  contentType: string;
  durationMs: number;
};

/**
 * The notes about one pupil, newest first.
 *
 * Bounded like the recitation history and for the same reason: the rules
 * answer parentage by READING the pupil's document once per returned row,
 * and Firestore caps how many such reads one request may make.
 */
export function useVoiceNotes(
  studentId: string | null,
): { notes: VoiceNote[]; loading: boolean } {
  const [state, setState] = useState<{ notes: VoiceNote[]; loading: boolean }>(
    { notes: [], loading: true },
  );

  useEffect(() => {
    if (!studentId) { setState({ notes: [], loading: false }); return undefined; }
    setState({ notes: [], loading: true });
    return onSnapshot(
      query(
        collection(db, 'voiceNotes'),
        where('studentId', '==', studentId),
        orderBy('createdAt', 'desc'),
        limit(20),
      ),
      (snap) => setState({
        notes: snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            studentId: String(data.studentId ?? ''),
            muqriId: String(data.muqriId ?? ''),
            durationMs: typeof data.durationMs === 'number' ? data.durationMs : 0,
            createdAt: typeof data.createdAt === 'number' ? data.createdAt : 0,
            expiresAt: typeof data.expiresAt === 'number' ? data.expiresAt : 0,
            heardAt: typeof data.heardAt === 'number' ? data.heardAt : null,
          };
        }),
        loading: false,
      }),
      () => setState({ notes: [], loading: false }),
    );
  }, [studentId]);

  return state;
}

/**
 * How many notes about this pupil nobody has listened to yet — م-3-06's
 * in-app half.
 *
 * A note the guardian has not heard is the thing to point at; one the
 * teacher recorded and replayed themselves is not new to anybody.
 */
export function unheardCount(notes: VoiceNote[], now: number): number {
  return notes.filter((n) => n.heardAt === null && now < n.expiresAt).length;
}

/** Records a note about a pupil. The audio is base64; the server bounds it. */
export async function saveVoiceNote(input: {
  studentId: string;
  audio: string;
  contentType: string;
  durationMs: number;
  sessionId?: string | null;
}): Promise<{ noteId: string; expiresAt: number }> {
  const call = httpsCallable<typeof input, { noteId: string; expiresAt: number }>(
    fns(), 'saveVoiceNote',
  );
  const out = await call(input);
  return out.data;
}

/** The audio itself, for somebody entitled to hear it. Never a link. */
export async function playVoiceNote(noteId: string): Promise<PlayableNote> {
  const call = httpsCallable<{ noteId: string }, PlayableNote>(fns(), 'playVoiceNote');
  const out = await call({ noteId });
  return out.data;
}

/** أ-04: deletion is the guardian's to ask for, and takes the audio with it. */
export async function deleteVoiceNote(noteId: string): Promise<void> {
  const call = httpsCallable<{ noteId: string }, { ok: boolean }>(fns(), 'deleteVoiceNote');
  await call({ noteId });
}

/**
 * Erases a child's account and everything it produced — أ-04, and only for
 * the parent who owns the account.
 */
/**
 * Erasing your own account, from inside the app.
 *
 * App Review 5.1.1(v), 2026-09-23: an app that lets people create an account
 * must let them delete it without writing to support. The callable takes the
 * managed children with it — a child account cannot sign in on its own, so
 * one left behind is unreachable.
 */
export async function deleteMyAccount(): Promise<void> {
  const call = httpsCallable<Record<string, never>, { deleted: boolean }>(
    fns(), 'deleteMyAccount',
  );
  await call({});
}

export async function deleteChildAccount(childUid: string): Promise<void> {
  const call = httpsCallable<{ childUid: string }, { ok: boolean }>(
    fns(), 'deleteChildAccount',
  );
  await call({ childUid });
}
